/**
 * iOS/Android standalone-PWA checkout helpers.
 *
 * Why this exists:
 *  - When the site is launched from a "Add to Home Screen" icon, iOS Safari
 *    puts the page in standalone mode. In standalone mode, a plain
 *    `window.location.href = <external_url>` to a domain like checkout.stripe.com
 *    *silently fails*. The button appears to do nothing.
 *  - The fix is to open the external URL in a separate browser tab/window
 *    (which iOS hands off to Safari properly). To avoid the "popup blocked"
 *    issue, the blank window must be opened *synchronously inside the click
 *    gesture* — i.e. before any async await — and then redirected once the
 *    Stripe checkout URL arrives from the backend.
 */

export const isStandalonePWA = () => {
  if (typeof window === "undefined") return false;
  try {
    if (window.matchMedia && window.matchMedia("(display-mode: standalone)").matches) {
      return true;
    }
  } catch (_) { /* older browsers */ }
  // iOS Safari legacy flag
  return window.navigator && window.navigator.standalone === true;
};

/**
 * Call this synchronously at the top of your click handler, BEFORE any await.
 * Returns a window handle (or null if not in PWA mode / popup blocked).
 */
export const prepareCheckoutWindow = () => {
  if (!isStandalonePWA()) return null;
  try {
    const w = window.open("about:blank", "_blank");
    if (!w) return null;
    try {
      w.document.write(
        '<!doctype html><meta name="viewport" content="width=device-width,initial-scale=1">' +
        '<title>Loading secure checkout…</title>' +
        '<style>body{margin:0;background:#0a0a0a;color:#fff;font-family:-apple-system,BlinkMacSystemFont,sans-serif;' +
        'display:flex;align-items:center;justify-content:center;min-height:100vh;text-align:center;padding:24px}' +
        '.spin{width:40px;height:40px;border:3px solid rgba(255,255,255,.15);border-top-color:#8B5CF6;' +
        'border-radius:50%;animation:s 0.8s linear infinite;margin:0 auto 16px}' +
        '@keyframes s{to{transform:rotate(360deg)}}</style>' +
        '<div><div class="spin"></div><p>Opening secure checkout…</p></div>'
      );
    } catch (_) { /* cross-origin edge, ignore */ }
    return w;
  } catch (_) {
    return null;
  }
};

/**
 * Finish the checkout redirect. If a PWA tab was pre-opened, redirect it.
 * Otherwise, redirect the current window (normal browser path).
 */
export const completeCheckoutRedirect = (pwaWindow, url) => {
  if (!url) return;
  if (pwaWindow && !pwaWindow.closed) {
    try {
      pwaWindow.location.href = url;
      return;
    } catch (_) {
      // If we can't set location (very rare), fall through to anchor fallback.
    }
  }
  if (isStandalonePWA()) {
    // Popup was blocked — click a synthetic anchor as a last resort.
    const a = document.createElement("a");
    a.href = url;
    a.target = "_blank";
    a.rel = "noopener";
    document.body.appendChild(a);
    a.click();
    a.remove();
    return;
  }
  window.location.href = url;
};

/**
 * If the pre-opened PWA tab must be closed on error.
 */
export const closeCheckoutWindow = (pwaWindow) => {
  if (pwaWindow && !pwaWindow.closed) {
    try { pwaWindow.close(); } catch (_) { /* no-op */ }
  }
};
